import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import {
	MarketingCTARow,
	MarketingSignup,
} from '@sy/app-client/src/ssrAdapter/components/Marketing';
import { mixins } from '@sy/component-library';

import ReducedBlogPost from '~/scenes/Blog/scenes/components/ReducedBlogPost';
import type {
	CacheBlogPost,
	CacheReducedBlogPost,
} from '~/services/contentful/model/ContentfulBlogPost';
import { events } from '~/services/tracking';
import type { TrackProperties } from '~/services/tracking/types';
import { useMediaQuery } from '~/utils/hooks';
import { Content, Header, SocialShare } from './components';
import { REDUCED_BLOG_POST_IMAGE_SIZES } from './constants';
import {
	StyledBlogPost,
	StyledMain,
	StyledRelatedPosts,
	StyledRelatedPostsContainer,
	StyledRelatedPostsTitle,
} from './BlogPost.styles';

const { mediaQueries } = mixins;

const BlogPost = ({ pageUrl, post, relatedPosts }: Props) => {
	const { t } = useTranslation('blog');
	const isLargeScreen = useMediaQuery(mediaQueries.large);

	const trackContentClick = useCallback((properties: TrackProperties) => {
		analytics.track(events.CONTENT_CLICKED, properties);
	}, []);

	useEffect(() => {
		analytics.page('Content', 'Blog', {
			contentType: 'Blog',
			estimatedPostLength: post.content?.length,
			pageType: 'Content',
			pageCategory: post.category,
		});
	}, [post]);

	return (
		<>
			<StyledBlogPost>
				<SocialShare contentName={post.title} pageUrl={pageUrl} />
				<StyledMain>
					<Header post={post} />
					<Content post={post} />
				</StyledMain>
			</StyledBlogPost>
			{relatedPosts.length > 0 && (
				<StyledRelatedPosts>
					<StyledRelatedPostsTitle>
						{t('blog.post.relatedPosts')}
					</StyledRelatedPostsTitle>
					<StyledRelatedPostsContainer>
						{relatedPosts.map((relatedPost, k) => (
							<ReducedBlogPost
								key={relatedPost.id}
								onClick={() =>
									trackContentClick({
										destinationContentName: relatedPost.title,
										contentType: 'Blog',
										destinationUrl: relatedPost.urlPath,
										location: 'related post panel',
										position: k + 1,
									})
								}
								post={relatedPost}
								shouldShowSummary={isLargeScreen}
								imageSizes={REDUCED_BLOG_POST_IMAGE_SIZES}
							/>
						))}
					</StyledRelatedPostsContainer>
				</StyledRelatedPosts>
			)}
			<MarketingCTARow cta={<MarketingSignup />} title={t('blog.cta.title')} />
		</>
	);
};

type Props = {
	pageUrl: string;
	post: CacheBlogPost;
	relatedPosts: CacheReducedBlogPost[];
};

export default BlogPost;
